import { c as _c } from "react/compiler-runtime";
import Map, { Source, Layer, NavigationControl, MapRef } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { SymbolPaint, SymbolLayout } from 'mapbox-gl';
import { match, P } from 'ts-pattern';
import { groupBy, mapValues, unique } from 'remeda';
import { notNull } from 'shared/type';
import { shapeData, asFeatureCollection, textMatchesShape, cityData, textMatchesCity } from './ukraineMap';
import { colors } from 'components/theme/theme';
import geoExtent from '@mapbox/geojson-extent';
const hoverOrActive = ['any', ['get', 'hover'], ['get', 'highlight']];
const paintDefaults: SymbolPaint = {
  'text-halo-blur': 1,
  'text-halo-color': colors.gray[1],
  'text-color': colors.gray[9],
  'text-halo-width': ['case', ['get', 'hover'], 2, 0.5]
};
const symbol = {
  paint: {
    shape: {
      ...paintDefaults,
      'text-color': colors.gray[6],
      'text-opacity': ['case', ['==', ['get', 'Level'], 'ADM0'], 0, ['get', 'filtered'], 0.6, 1]
    },
    city: {
      ...paintDefaults,
      'icon-opacity': 1,
      'icon-halo-width': 1,
      'icon-halo-blur': 1
    }
  } satisfies Record<string, SymbolPaint>,
  layout: {
    shape: {
      'text-size': ['case', ['get', 'filtered'], 10, 13],
      'text-font': ['Open Sans Bold'],
      'text-field': '{shapeName}'
    },
    city: {
      'text-font': ['Open Sans SemiBold'],
      'icon-image': 'dot-10',
      'icon-size': ['case', ['get', 'hover'], 2, 1.2],
      'icon-offset': [0, 0],
      'text-offset': [0, 0.8],
      'text-field': '{name}',
      'text-size': ['case', ['get', 'hover'], 20, ['get', 'filtered'], 10, 14]
    }
  } satisfies Record<string, SymbolLayout>
};
const defaultHoverState = {
  shape: [] as string[],
  city: [] as string[]
};
const UkraineMap = t0 => {
  const $ = _c(33);
  const {
    hoverText: causesText,
    contextText,
    height
  } = t0;
  const mapRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      queryKey: ["shape"],
      queryFn: _temp2
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    data: shapes
  } = useQuery(t1);
  const [hover] = useState(defaultHoverState);
  if (!shapes) {
    let t2;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <></>;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["shape", "city"];
    t3 = {
      latitude: 47.21519475420334,
      longitude: 36.65031197919154,
      zoom: 5
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] !== shapes) {
    t4 = t5 => {
      const {
        target: loadedMap
      } = t5;
      const geo = asFeatureCollection(shapes.filter(_temp3));
      const [w, s_0, e, n] = geoExtent(geo);
      loadedMap.resize();
      loadedMap.fitBounds([[w, s_0], [e, n]], {
        padding: 0,
        duration: 50
      });
    };
    $[4] = shapes;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== height) {
    t5 = {
      width: "100%",
      height
    };
    $[6] = height;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <NavigationControl showZoom={true} position="top-right" showCompass={false} />;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== causesText || $[10] !== contextText || $[11] !== hover || $[12] !== shapes) {
    t7 = asFeatureCollection(shapes?.map(s_1 => ({
      ...s_1,
      properties: {
        ...s_1.properties,
        hover: hover.shape.includes(s_1.properties.shapeName) || causesText != null && textMatchesShape(causesText, s_1.properties.shapeName),
        filtered: contextText && !contextText?.some(t => textMatchesShape(t, s_1.properties.shapeName))
      }
    })) ?? []);
    $[9] = causesText;
    $[10] = contextText;
    $[11] = hover;
    $[12] = shapes;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = ["case", ["get", "filtered"], colors.gray[5], colors.teal[7]];
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = ["get", "hover"];
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Layer type="fill" id="shape" paint={{
      "fill-color": t8,
      "fill-opacity": ["case", t9, 0.5, ["get", "filtered"], 0.1, 0.3]
    }} />;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  let t12;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Layer type="line" paint={{
      "line-color": colors.teal[8],
      "line-opacity": 0.5
    }} />;
    t12 = <Layer type="symbol" layout={symbol.layout.shape} paint={symbol.paint.shape} />;
    $[17] = t11;
    $[18] = t12;
  } else {
    t11 = $[17];
    t12 = $[18];
  }
  let t13;
  if ($[19] !== t7) {
    t13 = <Source type="geojson" data={t7}>{t10}{t11}{t12}</Source>;
    $[19] = t7;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== causesText || $[22] !== contextText || $[23] !== hover) {
    t14 = asFeatureCollection(cityData.map(s_2 => ({
      ...s_2,
      properties: {
        ...s_2.properties,
        hover: hover.shape.includes(s_2.properties.name) || causesText != null && textMatchesCity(causesText, s_2.properties.name),
        filtered: contextText && !contextText?.some(t_0 => textMatchesCity(t_0, s_2.properties.name))
      }
    })) ?? []);
    $[21] = causesText;
    $[22] = contextText;
    $[23] = hover;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Layer id="city" type="symbol" layout={symbol.layout.city} paint={symbol.paint.city} />;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== t14) {
    t16 = <Source type="geojson" data={t14}>{t15}</Source>;
    $[26] = t14;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== t13 || $[29] !== t16 || $[30] !== t4 || $[31] !== t5) {
    t17 = <Map ref={mapRef} id="map" scrollZoom={false} boxZoom={true} interactiveLayerIds={t2} initialViewState={t3} trackResize={true} onLoad={t4} onMouseMove={_temp11} style={t5} mapStyle="mapbox://styles/markledwich/cl90za077002v16o7njz8nxor" mapboxAccessToken={process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN}>{t6}{t13}{t16}</Map>;
    $[28] = t13;
    $[29] = t16;
    $[30] = t4;
    $[31] = t5;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  return t17;
};
export default UkraineMap;
function _temp(d) {
  return d;
}
function _temp2() {
  return shapeData().then(_temp);
}
function _temp3(s) {
  return s.properties.shapeName != "Ukraine";
}
function _temp4(p) {
  return p.Level != "ADM0" ? {
    type: "city",
    name: p.name ?? "unknown"
  } : null;
}
function _temp5(t0) {
  const {
    shapeName
  } = t0;
  return {
    type: "shape",
    name: shapeName
  };
}
function _temp6() {
  return null;
}
function _temp7(f) {
  return match(f.properties).with({
    Level: P.string,
    name: P.string
  }, _temp4).with({
    shapeName: P.string
  }, _temp5).otherwise(_temp6);
}
function _temp8(w_0) {
  return w_0.type;
}
function _temp9(w_1) {
  return w_1.name;
}
function _temp10(v) {
  return unique(v.map(_temp9));
}
function _temp11(e_0) {
  if (!e_0.features) {
    return;
  }
  const words = e_0.features.map(_temp7).filter(notNull);
  mapValues(groupBy(words, _temp8), _temp10);
}